import type { AnySchema, Asserts } from 'yup';
import * as yup from 'yup';

const requiredInt = () => yup.number().integer().required();

const requiredNumber = () => yup.number().required();

const turnoverReasonDatumSchema = yup
  .object({
    reason: yup.string().defined(),
    count: requiredInt(),
    ordinal: requiredInt(),
  })
  .strict(true);

const turnoverReasonsSchema = yup
  .object({
    voluntary: requiredInt(),
    involuntary: requiredInt(),
    // TODO(HRM-39): tighten back to required() once BE >= SCHEMA_VERSION 3 is everywhere
    notSpecified: yup.number().integer().notRequired(),
    voluntaryReasons: yup.array(turnoverReasonDatumSchema).notRequired(),
    involuntaryReasons: yup.array(turnoverReasonDatumSchema).notRequired(),
  })
  .strict(true)
  .required();

const turnoverMomRowSchema = yup
  .object({
    month: requiredInt(),
    employeesAtStart: requiredInt(),
    employeesAtEnd: requiredInt(),
    employeesWhoLeft: requiredInt(),
    voluntaryLeft: requiredInt(),
    involuntaryLeft: requiredInt(),
    probationFailures: requiredInt(),
    newHires: requiredInt(),
    newHiresLeftWithinFirstYear: requiredInt(),
  })
  .strict(true);

const turnoverProjectRowSchema = yup
  .object({
    project: yup.string().required(),
    employeesWhoLeft: requiredInt(),
    // TODO(HRM-39): tighten back to required() once BE >= SCHEMA_VERSION 3 is everywhere
    voluntaryLeft: yup.number().integer().notRequired(),
    involuntaryLeft: yup.number().integer().notRequired(),
    headcountAtStart: yup.number().integer().notRequired(),
    headcountAtEnd: yup.number().integer().notRequired(),
    turnoverRate: requiredNumber(),
  })
  .strict(true);

const turnoverProjectGroupRowSchema = yup
  .object({
    // defined() (not required): empty string must not fail the whole dashboard payload
    group: yup.string().defined(),
    projectCount: requiredInt(),
    employeesWhoLeft: requiredInt(),
    // TODO(HRM-39): tighten back to required() once BE >= SCHEMA_VERSION 3 is everywhere
    voluntaryLeft: yup.number().integer().notRequired(),
    involuntaryLeft: yup.number().integer().notRequired(),
    headcountAtStart: yup.number().integer().notRequired(),
    headcountAtEnd: yup.number().integer().notRequired(),
    turnoverRate: requiredNumber(),
  })
  .strict(true);

const turnoverYearlyDatumRowSchema = yup
  .object({
    year: requiredInt(),
    employeesAtStart: requiredInt(),
    employeesAtEnd: requiredInt(),
    employeesWhoLeft: requiredInt(),
    voluntaryLeft: requiredInt(),
    involuntaryLeft: requiredInt(),
    probationFailures: requiredInt(),
    newHires: requiredInt(),
    newHiresLeftWithinFirstYear: requiredInt(),
    retentionRate: requiredNumber(),
    turnoverRate: requiredNumber(),
  })
  .strict(true);

export const turnoverDashboardApiBodySchema = yup
  .object({
    year: requiredInt(),
    employeesAtStart: requiredInt(),
    employeesAtEnd: requiredInt(),
    employeesWhoLeft: requiredInt(),
    voluntaryLeft: requiredInt(),
    involuntaryLeft: requiredInt(),
    probationFailures: requiredInt(),
    newHires: requiredInt(),
    newHiresLeftWithinFirstYear: requiredInt(),
    reasons: turnoverReasonsSchema,
    mom: yup.array(turnoverMomRowSchema).length(12).required(),
    projects: yup.array(turnoverProjectRowSchema).required(),
    // TODO(HRM-39): tighten back to required() once BE >= SCHEMA_VERSION 3 is everywhere
    projectGroups: yup.array(turnoverProjectGroupRowSchema).notRequired(),
    yearlyTrend: yup.array(turnoverYearlyDatumRowSchema).nullable().notRequired(),
    // TODO(HRM-39): tighten back to required() once BE >= SCHEMA_VERSION 4 is everywhere.
    // Absent means the backend predates the like-for-like comparison, and the KPI cards
    // suppress their year-over-year delta rather than compare unequal spans.
    priorYearToDate: turnoverYearlyDatumRowSchema.nullable().notRequired(),
    // Only the one field is read; the rest of meta stays unparsed on purpose.
    meta: yup
      .object({ reasonVocabularySize: yup.number().integer().notRequired() })
      .nullable()
      .notRequired(),
  })
  .strict(true);

export const turnoverYearlyTrendEnvelopeSchema = yup
  .object({
    data: yup.array(turnoverYearlyDatumRowSchema).required(),
  })
  .strict(true);

export function assertTurnoverApiValid<S extends AnySchema>(schema: S, value: unknown): Asserts<S> {
  try {
    return schema.validateSync(value, { abortEarly: false, strict: true });
  } catch (e: unknown) {
    if (e instanceof yup.ValidationError) {
      throw new Error(`Turnover API: ${e.errors.join('; ')}`);
    }
    throw e;
  }
}
